import { parseDate } from './dates';
import type { Member, Role, ScheduleEvent } from './types';

/**
 * Regras avançadas da função (Ministérios › card da função › "Regras avançadas"):
 * função crítica, experiência mínima, tempo de casa mínimo, membros em treinamento, pares
 * que não servem juntos e pares que servem juntos. Valem na geração automática (que não as
 * viola e explica as vagas que ficaram sem pessoa), na escolha manual (o seletor avisa e pede
 * confirmação — nunca bloqueia em silêncio) e na explicação da matriz de decisão. Os pares
 * que servem juntos são preferência: a geração encaixa o par na mesma escala quando pode, e
 * o seletor sugere o parceiro de quem já está na função.
 */

/** Função sem nenhuma regra avançada — o padrão de uma função nova. */
export const NO_ROLE_RULES: Pick<
  Role,
  'critical' | 'minExperience' | 'minYears' | 'trainees' | 'blockedPairs' | 'togetherPairs'
> = {
  critical: false,
  minExperience: 0,
  minYears: 0,
  trainees: [],
  blockedPairs: [],
  togetherPairs: [],
};

/** Limites dos contadores do painel (iguais aos do contrato da API). */
export const MAX_MIN_EXPERIENCE = 50;
export const MAX_MIN_YEARS = 50;

/**
 * Quem serviu em cada função do evento, de fato: os escalados, menos quem faltou no
 * check-in, mais quem serviu no lugar de alguém (na função de quem faltou).
 */
export function servedByRole(event: ScheduleEvent): Record<string, string[]> {
  const out: Record<string, string[]> = {};

  for (const [roleId, ids] of Object.entries(event.slots)) {
    const list: string[] = [];

    for (const id of ids) {
      const mark = event.attendance[id];

      if (mark?.status !== 'absent') list.push(id);
      else if (mark.substituteId) list.push(mark.substituteId);
    }

    out[roleId] = [...new Set(list)];
  }

  return out;
}

/**
 * Índice de experiência: em que datas cada pessoa serviu em cada função. A experiência
 * numa data é quantas vezes serviu antes dela — escalas anteriores contam, as de depois
 * não. A geração acrescenta as escolhas que faz (`add`) e desfaz as que troca (`remove`).
 */
export interface ExperienceIndex {
  count: (memberId: string, roleId: string, beforeDate: string) => number;
  add: (memberId: string, roleId: string, date: string) => void;
  remove: (memberId: string, roleId: string, date: string) => void;
}

export function experienceIndex(events: readonly ScheduleEvent[]): ExperienceIndex {
  const dates = new Map<string, string[]>();
  const add = (memberId: string, roleId: string, date: string) => {
    const key = `${memberId}|${roleId}`;
    dates.set(key, [...(dates.get(key) ?? []), date]);
  };

  for (const event of events) {
    for (const [roleId, ids] of Object.entries(servedByRole(event))) {
      ids.forEach((id) => add(id, roleId, event.date));
    }
  }

  return {
    count: (memberId, roleId, beforeDate) =>
      (dates.get(`${memberId}|${roleId}`) ?? []).filter((d) => d < beforeDate).length,
    add,
    remove: (memberId, roleId, date) => {
      const key = `${memberId}|${roleId}`;
      const list = dates.get(key) ?? [];
      const i = list.lastIndexOf(date);

      if (i >= 0) dates.set(key, [...list.slice(0, i), ...list.slice(i + 1)]);
    },
  };
}

/** Anos completos de membresia na data (`null` sem "Membro desde"). */
export function yearsOfMembership(memberSince: string | null, onDate: string): number | null {
  if (!memberSince || memberSince > onDate) return memberSince ? 0 : null;

  const since = parseDate(memberSince);
  const on = parseDate(onDate);
  let years = on.getFullYear() - since.getFullYear();

  if (
    on.getMonth() < since.getMonth() ||
    (on.getMonth() === since.getMonth() && on.getDate() < since.getDate())
  ) {
    years--;
  }

  return Math.max(0, years);
}

export function isTrainee(role: Role, memberId: string): boolean {
  return role.trainees.includes(memberId);
}

function partnersIn(pairs: readonly (readonly [string, string])[], memberId: string): string[] {
  return pairs.flatMap(([a, b]) => {
    if (a === memberId) return [b];
    if (b === memberId) return [a];

    return [];
  });
}

/** Quem não pode servir junto com a pessoa, pelos pares desta função. */
export function pairPartners(role: Role, memberId: string): string[] {
  return partnersIn(role.blockedPairs, memberId);
}

/** Quem serve junto com a pessoa nesta função (os pares que servem juntos). */
export function togetherPartners(role: Role, memberId: string): string[] {
  return partnersIn(role.togetherPairs, memberId).filter((id) => id !== memberId);
}

/**
 * O parceiro de "servem juntos" que já está na função `role` do evento, se algum está: quem
 * entra agora completa o par. Devolve o id dele (ou `null`).
 */
export function togetherPartnerIn(event: ScheduleEvent, role: Role, memberId: string): string | null {
  const inRole = event.slots[role.id] ?? [];

  return togetherPartners(role, memberId).find((id) => inRole.includes(id)) ?? null;
}

/**
 * Os pares da função que não fazem sentido: um membro com ele mesmo, ou o mesmo par entre os
 * que não servem juntos e os que servem juntos. `null` quando está tudo certo.
 */
export function rolePairsError(role: Pick<Role, 'blockedPairs' | 'togetherPairs'>): string | null {
  if (role.blockedPairs.some(([a, b]) => a === b))
    return 'Cada par restrito precisa de dois membros diferentes.';
  if (role.togetherPairs.some(([a, b]) => a === b))
    return 'Cada par que serve junto precisa de dois membros diferentes.';
  if (role.togetherPairs.some((p) => role.blockedPairs.some((q) => samePair(p, q))))
    return 'O mesmo par não pode estar entre os que não servem juntos e os que servem juntos.';

  return null;
}

/**
 * Revezamento do casal: a pessoa e o cônjuge escolheram "Revezar cultos" e o cônjuge já
 * está no evento, em qualquer função. Devolve o id do cônjuge (ou `null`).
 */
export function alternateConflict(event: ScheduleEvent, member: Member): string | null {
  const spouseId = member.spouseId;

  if (!spouseId || member.coupleMode !== 'alternate') return null;

  return Object.values(event.slots).some((ids) => ids.includes(spouseId)) ? spouseId : null;
}

/**
 * Par restrito: a pessoa entraria na função `role` do evento junto de alguém com quem não
 * pode servir. Vale nos dois sentidos — o par desta função com alguém em qualquer função
 * do evento, e o par de outra função do evento em que o parceiro já está.
 */
export function pairConflict(
  event: ScheduleEvent,
  memberId: string,
  role: Role,
  roles: readonly Role[]
): { partnerId: string; role: Role } | null {
  const inEvent = new Set(Object.values(event.slots).flat());

  for (const partnerId of pairPartners(role, memberId)) {
    if (partnerId !== memberId && inEvent.has(partnerId)) return { partnerId, role };
  }

  for (const other of roles) {
    if (other.id === role.id) continue;

    for (const partnerId of pairPartners(other, memberId)) {
      if ((event.slots[other.id] ?? []).includes(partnerId)) return { partnerId, role: other };
    }
  }

  return null;
}

/** Alguém experiente (fora do treinamento) já na função do evento, que acompanha o trainee. */
export function mentorOf(event: ScheduleEvent, role: Role, memberId: string): string | null {
  return (event.slots[role.id] ?? []).find((id) => id !== memberId && !isTrainee(role, id)) ?? null;
}

export type RoleRuleKind = 'pair' | 'couple' | 'trainee' | 'experience' | 'years';

export interface RoleRuleIssue {
  kind: RoleRuleKind;
  /** Rótulo curto do seletor ("Par restrito", "Em treinamento", "Pouca experiência"…). */
  title: string;
  detail: string;
}

export const ROLE_RULE_TITLE: Record<RoleRuleKind, string> = {
  pair: 'Par restrito',
  couple: 'Revezamento',
  trainee: 'Em treinamento',
  experience: 'Pouca experiência',
  years: 'Pouco tempo de casa',
};

export const experienceText = (n: number) => `${n} ${n === 1 ? 'escala anterior' : 'escalas anteriores'}`;
export const yearsText = (n: number) => `${n} ${n === 1 ? 'ano' : 'anos'}`;

/**
 * As regras avançadas que a pessoa não cumpre para entrar na função do evento, na ordem
 * em que a interface as mostra. `experience` é quantas vezes ela já serviu na função
 * antes do evento; `nameOf` dá o nome de quem é citado.
 */
export function roleRuleIssues(
  member: Member,
  role: Role,
  event: ScheduleEvent,
  roles: readonly Role[],
  experience: number,
  nameOf: (memberId: string) => string
): RoleRuleIssue[] {
  const issues: RoleRuleIssue[] = [];
  const pair = pairConflict(event, member.id, role, roles);

  if (pair) {
    issues.push({
      kind: 'pair',
      title: ROLE_RULE_TITLE.pair,
      detail: `${member.name} e ${nameOf(pair.partnerId)} não servem juntos (regra de ${pair.role.name}).`,
    });
  }

  if (alternateConflict(event, member)) {
    issues.push({
      kind: 'couple',
      title: ROLE_RULE_TITLE.couple,
      detail: `${member.name} reveza os eventos com o cônjuge, ${nameOf(member.spouseId!)}, já escalado(a) neste evento: o casal serve um por evento.`,
    });
  }

  if (isTrainee(role, member.id)) {
    if (!mentorOf(event, role, member.id)) {
      issues.push({
        kind: 'trainee',
        title: ROLE_RULE_TITLE.trainee,
        detail: `${member.name} está em treinamento em ${role.name} e só serve acompanhado(a) de alguém experiente nesta função.`,
      });
    }
  } else if (role.minExperience > 0 && experience < role.minExperience) {
    issues.push({
      kind: 'experience',
      title: ROLE_RULE_TITLE.experience,
      detail: `${role.name} exige ${experienceText(role.minExperience)}; ${member.name} tem ${experience}.`,
    });
  }

  if (role.minYears > 0) {
    const years = yearsOfMembership(member.memberSince, event.date);

    if (years === null || years < role.minYears) {
      issues.push({
        kind: 'years',
        title: ROLE_RULE_TITLE.years,
        detail:
          years === null
            ? `${role.name} exige ${yearsText(role.minYears)} de membresia, e o cadastro de ${member.name} não tem a data "Membro desde".`
            : `${role.name} exige ${yearsText(role.minYears)} de membresia; ${member.name} tem ${yearsText(years)}.`,
      });
    }
  }

  return issues;
}

/** Resumo das regras ativas, mostrado com o painel fechado. */
export function roleRulesSummary(role: Role): string {
  const parts = [
    role.critical ? 'Crítica' : '',
    role.minExperience > 0 ? `Mín. ${experienceText(role.minExperience)}` : '',
    role.minYears > 0 ? `${yearsText(role.minYears)} de casa` : '',
    role.trainees.length ? `${role.trainees.length} em treinamento` : '',
    role.blockedPairs.length
      ? `${role.blockedPairs.length} ${role.blockedPairs.length === 1 ? 'par restrito' : 'pares restritos'}`
      : '',
    role.togetherPairs.length
      ? `${role.togetherPairs.length} ${role.togetherPairs.length === 1 ? 'par junto' : 'pares juntos'}`
      : '',
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : 'Nenhuma regra ativa';
}

/** Mesmo par, em qualquer ordem. */
export function samePair(a: readonly [string, string], b: readonly [string, string]): boolean {
  return (a[0] === b[0] && a[1] === b[1]) || (a[0] === b[1] && a[1] === b[0]);
}
